import { ref, computed, watch } from 'vue'
import { toast } from '~/utils/sonner'
import { debounce } from 'lodash-es'
import { myescape, myunescape } from '~/utils/escape'

// Constants for field names and validation
export const BILLING_FIELDS = {
  FIRSTNAME: 'firstname',
  LASTNAME: 'lastname',
  ADDR: 'addr',
  CITY: 'city',
  STATE: 'state',
  ZIP: 'zip',
  COUNTRY: 'country',
  COMPANY_NAME: 'company_name',
  COMPANY_VAT: 'company_vat',
} as const

export const DEFAULT_COUNTRY = 'China'

interface BillingFormOptions {
  initialData?: Partial<BillingAddress>
  onUpdate?: (billing: BillingAddress) => Promise<void>
}

interface BillingFormState extends BillingAddress {
  firstname: string
  lastname: string
  addr: string
  city: string
  state: string
  zip: string
  country: string
  company_name?: string
  company_vat?: string
}

export function useBillingForm(options: BillingFormOptions = {}) {
  const $t = useI18n().t
  const $utils = useUtils()

  // Initialize empty billing state
  const emptyBilling: BillingFormState = {
    firstname: '',
    lastname: '',
    addr: '',
    city: '',
    state: '',
    zip: '',
    country: DEFAULT_COUNTRY,
    company_name: undefined,
    company_vat: undefined,
  }

  // Form state
  const formData = ref<BillingFormState>({ ...emptyBilling })
  const initialFormData = ref<BillingFormState>({ ...emptyBilling })
  const isUpdating = ref(false)
  const showCompanyFields = ref(false)

  // Initialize with provided data
  if (options.initialData) {
    const initialBilling = {
      ...emptyBilling,
      ...options.initialData,
    }
    formData.value = { ...initialBilling }
    initialFormData.value = { ...initialBilling }
    showCompanyFields.value = !!initialBilling.company_name
  }

  // Computed properties
  const isDirty = computed(() => {
    return JSON.stringify(formData.value) !== JSON.stringify(initialFormData.value)
  })

  const isPersonalNameRequired = computed(() => {
    return !showCompanyFields.value || !formData.value.company_name?.trim()
  })

  const hasCompanyInfo = computed(() => {
    return !!formData.value.company_name || showCompanyFields.value
  })

  const isEuCountry = computed(() => {
    return $utils.isEu(formData.value.country)
  })

  const vatReverseCharge = computed(() => {
    if (formData.value.company_vat && formData.value.country !== 'Netherlands') {
      return true
    }
    return false
  })

  // Check if user has saved/verified VAT reverse charge
  // This is true when the current form data matches what's saved in the database (already verified)
  const hasVerifiedVatReverseCharge = computed(() => {
    const user = useUser()
    // Check if current form has company info that matches saved billing
    if (
      formData.value.company_name &&
      formData.value.company_vat &&
      formData.value.country !== 'Netherlands' &&
      isEuCountry.value
    ) {
      // If it matches what's saved, it's verified
      if (
        user.billing?.company_vat === formData.value.company_vat &&
        user.billing?.company_name === formData.value.company_name
      ) {
        return true
      }
    }
    return false
  })

  // Check if user is currently entering company info for VAT reverse charge
  const isPendingVatReverseCharge = computed(() => {
    // User is entering company info but it's not yet saved/verified
    if (
      formData.value.company_name &&
      formData.value.company_vat &&
      formData.value.country !== 'Netherlands' &&
      isEuCountry.value
    ) {
      const user = useUser()
      // It's pending if it's different from what's saved
      if (
        !user.billing?.company_vat ||
        user.billing?.company_vat !== formData.value.company_vat ||
        user.billing?.company_name !== formData.value.company_name
      ) {
        return true
      }
    }
    return false
  })

  const vatRate = computed(() => {
    let rate = 0
    if (isEuCountry.value) {
      rate = 0.21
      if (vatReverseCharge.value) {
        rate = 0
      }
    }
    return rate
  })

  const isFormEmpty = computed(() => {
    return (
      !formData.value.firstname?.trim() &&
      !formData.value.lastname?.trim() &&
      !formData.value.addr?.trim() &&
      !formData.value.city?.trim() &&
      !formData.value.company_name?.trim()
    )
  })

  // Methods
  const validateForm = (): { valid: boolean; errors: string[] } => {
    const errors: string[] = []
    const checkFields = [
      BILLING_FIELDS.FIRSTNAME,
      BILLING_FIELDS.LASTNAME,
      BILLING_FIELDS.COUNTRY,
      BILLING_FIELDS.ADDR,
      BILLING_FIELDS.CITY,
    ]

    for (const field of checkFields) {
      if (!formData.value[field]?.trim()) {
        // Skip name fields if company name is provided
        if (
          formData.value.company_name?.trim() &&
          [BILLING_FIELDS.FIRSTNAME, BILLING_FIELDS.LASTNAME].includes(field as any)
        ) {
          continue
        }
        errors.push(`${field} is required`)
      }
    }

    // Validate company name if company fields are shown
    if (showCompanyFields.value && !formData.value.company_name?.trim()) {
      errors.push('Company name is required when entering company information')
    }

    return {
      valid: errors.length === 0,
      errors,
    }
  }

  const resetForm = () => {
    formData.value = { ...emptyBilling }
    initialFormData.value = { ...emptyBilling }
    showCompanyFields.value = false
  }

  const loadBillingData = (billing: Partial<BillingAddress>) => {
    const newBilling = { ...emptyBilling, ...billing }

    // Unescape data if needed
    for (const [key, val] of Object.entries(newBilling)) {
      if (typeof val === 'string') {
        newBilling[key] = myunescape(val.trim())
      }
    }

    formData.value = newBilling
    initialFormData.value = { ...newBilling }
    showCompanyFields.value = !!newBilling.company_name
  }

  const prepareBillingForSubmit = (): BillingAddress => {
    const billing: BillingAddress = {
      firstname: '',
      lastname: '',
      addr: '',
      city: '',
      state: '',
      zip: '',
      country: DEFAULT_COUNTRY,
      company_name: '',
      company_vat: '',
    }
    for (const [key, val] of Object.entries(formData.value)) {
      if (val !== undefined && val !== null) {
        // Escape values for storage
        // console.log('key:', key, 'escaping val:', val)
        billing[key] = typeof val === 'string' ? val.trim() : val
      }
    }

    return billing
  }

  const toggleCompanyFields = () => {
    showCompanyFields.value = !showCompanyFields.value
    if (!showCompanyFields.value) {
      formData.value.company_name = undefined
      formData.value.company_vat = undefined
    }
  }

  // Debounced VAT validation for EU countries
  const validateVat = debounce((vat: string) => {
    if (!vat || !isEuCountry.value) return

    // Format VAT number for EU countries
    if (formData.value.company_vat && isEuCountry.value) {
      formData.value.company_vat = formData.value.company_vat.toUpperCase()
    }
  }, 500)

  // Watch for VAT changes
  watch(
    () => formData.value.company_vat,
    (newVat) => {
      if (newVat) {
        validateVat(newVat)
      }
    },
  )

  return {
    // State
    formData,
    isUpdating,
    showCompanyFields,

    // Computed
    isDirty,
    isPersonalNameRequired,
    hasCompanyInfo,
    isEuCountry,
    vatReverseCharge,
    hasVerifiedVatReverseCharge,
    isPendingVatReverseCharge,
    vatRate,
    isFormEmpty,

    // Methods
    validateForm,
    resetForm,
    loadBillingData,
    prepareBillingForSubmit,
    toggleCompanyFields,
  }
}
